import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, bounceToAdmin, clearBounce, CF_ACCESS_LOGOUT_URL, isAuthBounce } from "./api";
import EvidencePanel from "./EvidencePanel";
import LlmStatus from "./LlmStatus";
import RunSummary from "./RunSummary";
import ReviewQueue from "./ReviewQueue";
import MyReviews from "./MyReviews";
import CodeEvidencePanel from "./CodeEvidencePanel";
import ProfilePanel from "./ProfilePanel";
import ItemModeration from "./ItemModeration";
import UpstreamReportPanel from "./UpstreamReportPanel";
import SecondOpinionPanel from "./SecondOpinionPanel";
import CautionBadges from "./CautionBadges";
import AutoConfirm from "./AutoConfirm";
import { BlockButton, Blocklist, ModerationBar, PrivacyRequests, RowCheck, UserReports, useSelection } from "./Moderation";
import CapabilityBadges from "@/components/brand/CapabilityBadges";
import RescoreJobPanel from "./RescoreJobPanel";
import AdminErrorBoundary from "./AdminErrorBoundary";
import ItemsPage from "./ItemsPage";
import StatsPage from "./StatsPage";
import SourcesOverview from "./SourcesOverview";
import { TIER_TONE } from "@/components/brand/StatusBadge";
import Logo from "@/components/brand/Logo";
import ThemeToggle from "@/components/brand/ThemeToggle";
import { JWKS_URL } from "../lib/config";
import { RawTextViewer, DiffViewer } from "./RawTextViewer";
import { Time, TimeZoneProvider, TimeZoneSelect } from "./time";

const PAGES = [
  { to: "/admin",             label: "Dashboard" },
  { to: "/admin/review",      label: "Review" },
  { to: "/admin/auto-confirm", label: "Auto-confirm" },
  { to: "/admin/items",       label: "Items" },
  { to: "/admin/sources",     label: "Sources" },
  { to: "/admin/stats",       label: "Stats" },
  { to: "/admin/disclosures", label: "Disclosures" },
  { to: "/admin/appeals",     label: "Appeals" },
  { to: "/admin/submissions", label: "Submissions" },
  { to: "/admin/waitlist",    label: "Waitlist" },
  { to: "/admin/user-reports", label: "User reports" },
  { to: "/admin/profiles",    label: "Profiles" },
  { to: "/admin/blocklist",   label: "Blocklist" },
  { to: "/admin/privacy",     label: "Privacy requests" },
  { to: "/admin/reports",     label: "Reports" },
  { to: "/admin/api-keys",    label: "API keys" },
  { to: "/admin/signing-keys", label: "Signing keys" },
  { to: "/admin/audit",       label: "Audit" },
];

// Admin styling uses the brand tokens (CSS variables from brand/tokens.css) only: no raw colours.
const styles = {
  bg: { background: "var(--hs-bg)", color: "var(--hs-text)", minHeight: "100vh", fontFamily: "var(--hs-font-body)" },
  card: { background: "var(--hs-surface)", border: "1px solid var(--hs-border)", borderRadius: 16, padding: 16, marginBottom: 16,
    overflowX: "auto", maxWidth: "100%", boxSizing: "border-box" },
  input: { background: "var(--hs-bg)", color: "var(--hs-text)", border: "1px solid var(--hs-border)", padding: "8px 12px",
    borderRadius: 6, fontSize: 14, minHeight: 44, boxSizing: "border-box", maxWidth: "100%", fontFamily: "var(--hs-font-body)" },
  btn: { background: "var(--hs-accent)", color: "var(--hs-accent-contrast)", border: "1px solid transparent", padding: "0 20px",
    borderRadius: 10, cursor: "pointer", fontWeight: 500, minHeight: 44, fontFamily: "var(--hs-font-display)", fontSize: 14 },
  btnDim: { background: "transparent", color: "var(--hs-text)", border: "1px solid var(--hs-border)", padding: "0 12px",
    borderRadius: 10, cursor: "pointer", minHeight: 44, fontFamily: "var(--hs-font-display)", fontWeight: 500, fontSize: 14 },
  tbl: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--hs-border)", color: "var(--hs-text-muted)", fontWeight: 500,
    fontFamily: "var(--hs-font-display)" },
  td: { padding: "8px 10px", borderBottom: "1px solid var(--hs-border)" },
  select: { background: "var(--hs-bg)", color: "var(--hs-text)", border: "1px solid var(--hs-border)", padding: "8px 12px",
    borderRadius: 6, fontSize: 14, minHeight: 44, maxWidth: "100%", fontFamily: "var(--hs-font-body)" },
  row: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 },
};
// Status tones (brand status colours); the label text is always shown.
const TIER_COLORS = TIER_TONE;
const STATUS_COLORS = { open: "warning", upheld: "safe", rejected: "danger", new: "info", in_review: "warning", accepted: "safe",
  pending: "warning", acknowledged: "safe", removed: "safe", disputed: "danger", no_response: "neutral" };

function Tag({ children, color = "neutral", testid }) {
  return <span data-testid={testid} className={`hs-badge hs-badge-${color}`}>{children}</span>;
}

function ErrorNote({ message, onRetry }) {
  const detail = typeof message === "string" ? message : (message && message.message) || "The admin request failed.";
  return (
    <div data-testid="error-note" role="alert" style={{ ...styles.card, borderColor: "var(--hs-danger)", color: "var(--hs-text)" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Something went wrong</div>
      <div style={{ marginBottom: 10, color: "var(--hs-text-muted)" }}>{detail}</div>
      {onRetry && (
        <button type="button" style={styles.btn} onClick={onRetry}>Retry</button>
      )}
    </div>
  );
}

function BackendDown({ error, onRetry }) {
  const message = error && (error.message || error.detail || "The backend is not responding.");
  const statusUrl = process.env.REACT_APP_STATUS_URL;
  return (
    <div data-testid="backend-down" role="alert" style={{ ...styles.bg, padding: 24 }}>
      <div style={{ ...styles.card, borderColor: "var(--hs-danger)" }}>
        <h2 style={{ marginTop: 0 }}>Backend not responding</h2>
        <p style={{ marginBottom: 12, color: "var(--hs-text-muted)" }}>{message}</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" style={styles.btn} onClick={onRetry}>Retry</button>
          {statusUrl && (
            <a href={statusUrl} target="_blank" rel="noreferrer" className="hs-link">Check status page</a>
          )}
        </div>
      </div>
    </div>
  );
}

function useApi(fetcher, deps = []) {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result && result.data !== undefined ? result.data : result);
      return result;
    } catch (err) {
      setError(err);
      setData(undefined);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [fetcher].concat(deps));
  return { data, error, loading, reload };
}

function Nav({ me, onLogout }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const active = (to) => loc.pathname === to || (to !== "/admin" && loc.pathname.startsWith(to));
  return (
    <header className="hs-admin-nav">
      <nav data-testid="admin-nav" aria-label="Admin">
        <div className="hs-admin-nav-bar">
          {/* full page navigation to the public site (it is a different app shell) */}
          <a href="/" aria-label="HerdSafe home"><Logo variant="mark" alt="HerdSafe" /></a>
          <span className="hs-display" style={{ fontWeight: 700 }}>Admin</span>
          <button type="button" className="hs-icon-btn hs-admin-nav-toggle" data-testid="admin-menu-btn" aria-expanded={open}
                  aria-controls="admin-menu" onClick={() => setOpen(!open)}>{open ? "Close" : "Menu"}</button>
          <div className="hs-admin-user">
            {me && <span className="who" title={`${me.email_or_name} · ${me.mode}`}>{me.email_or_name}</span>}
            <TimeZoneSelect style={styles.select} />
            <ThemeToggle />
            <button data-testid="logout-btn" type="button" style={styles.btnDim} onClick={onLogout}>Logout</button>
          </div>
        </div>
        <div id="admin-menu" className={`hs-admin-nav-links${open ? " is-open" : ""}`}>
          {PAGES.map(p => (
            <Link key={p.to} to={p.to} data-testid={`nav-${p.label.toLowerCase()}`} className="hs-admin-link"
                  aria-current={active(p.to) ? "page" : undefined}>{p.label}</Link>
          ))}
        </div>
      </nav>
    </header>
  );
}

function Login({ onIn }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [fatal, setFatal] = useState("");
  useEffect(() => {
    // The server decides the mode. If we cannot learn it, show an error —
    // never guess "password".
    // A redirect / opaque / network failure means Cloudflare Access wants a fresh sign-in: do ONE top-level
    // navigation to /admin (loop-guarded) instead of showing an error.
    const bounceOr = (ex, msg) => {
      if (isAuthBounce(ex) && bounceToAdmin()) { setMode("redirecting"); return; }
      setFatal(isAuthBounce(ex) ? "Cloudflare Access sign-in did not complete. Close this tab and open /admin again." : msg);
    };
    api.authMode()
      .then(async ({ data }) => {
        clearBounce();
        setMode(data.mode);
        if (data.mode === "cf_access") {
          try { const me = await api.me(); onIn(me.data); }
          catch (ex) { bounceOr(ex, `Cloudflare Access session not accepted (${ex.message}). Reload to sign in again.`); }
        }
      })
      .catch((ex) => bounceOr(ex, `Could not determine admin auth mode (${ex.message}).`));
  }, [onIn]);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.login(pw); const { data } = await api.me(); onIn(data); }
    catch (ex) { setErr(ex.message); }
  };
  if (fatal) return (
    <div data-testid="auth-mode-error" role="alert" style={{ ...styles.bg, padding: 24 }}>
      <span className="hs-text-danger">{fatal}</span>{" "}<a className="hs-link" href="/admin">Open /admin</a>
    </div>);
  if (mode !== "password") return (
    <div style={{ ...styles.bg, padding: 24 }} data-testid="admin-signing-in">
      {mode === "cf_access" ? "Signing in via Cloudflare Access…" : mode === "redirecting" ? "Redirecting to sign-in…" : "…"}
    </div>);
  return (
    <div style={{ ...styles.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <form onSubmit={submit} style={{ ...styles.card, width: "100%", maxWidth: 360 }} data-testid="login-form">
        <div style={{ marginBottom: 16 }}><Logo variant="horizontal" alt="HerdSafe" className="hs-login-logo" /></div>
        <h1 style={{ marginTop: 0, fontSize: 22 }}>Admin sign-in</h1>
        <p style={{ fontSize: 14, color: "var(--hs-text-muted)" }}>mode: {mode}</p>
        <label className="hs-label" htmlFor="admin-password">Password</label>
        <input id="admin-password" data-testid="password-input" type="password" value={pw} onChange={e => setPw(e.target.value)}
               style={{ ...styles.input, width: "100%", marginBottom: 10 }} />
        {err && <div data-testid="login-error" role="alert" className="hs-text-danger" style={{ fontSize: 14, marginBottom: 10 }}>{err}</div>}
        <button data-testid="login-submit" type="submit" style={styles.btn}>Sign in</button>
      </form>
    </div>
  );
}

function Dashboard() {
  const [jobId, setJobId] = useState(null);
  const loadDashboard = useCallback(() => api.dashboard(), []);
  const { data, error, loading, reload } = useApi(loadDashboard, []);
  if (!data && !error && loading) return <div>Loading…</div>;
  if (error) return <ErrorNote message={error} onRetry={reload} />;
  const max = Math.max(1, ...data.confirmed_last_6_months_by_month.map(b => b.count));
  const triggerRulesRescore = async (onlyStale) => {
    if (!window.confirm((onlyStale ? "Score the items not yet analysed with the current rules?" :
      "Re-run only the rules over EVERY item's latest snapshot?") + " It runs in the background in batches, survives " +
      "restarts and can be paused / resumed. The model is called only for items that newly cross the gate (within the daily cap).")) return;
    const r = await api.rescoreRules({ only_stale: !!onlyStale });
    setJobId(r.data.job_id);
  };
  const triggerRescore = async () => {
    const r = await api.rescore({ since: "all" });
    setJobId(r.data.job_id);
  };
  const tiles = [
    ["Confirmed this month", data.confirmed_this_month, "tile-confirmed"],
    ["Precision (human)", data.precision_from_human_labels ?? "—", "tile-precision"],
    ["Human labels (30d)", data.human_label_count_30d ?? 0, "tile-humans"],
    ["LLM today A$", data.llm_spend_today_aud, "tile-llm-today"],
    ["LLM month A$", data.llm_spend_month_aud, "tile-llm-month"],
    ["Median lead time (days)", data.median_lead_time_days ?? "—", "tile-lead"],
    ["Beat-public rate", data.beat_public_rate ?? "—", "tile-beat"],
    ["Disclosure ack rate", data.disclosure_ack_rate ?? "—", "tile-ack"],
  ];
  return (
    <div data-testid="dashboard-page">
      <div style={styles.row}>
        <h2 style={{ margin: 0 }}>Dashboard</h2>
        <div style={{ marginLeft: "auto" }}>
          <button data-testid="rescore-remaining-btn" style={{ ...styles.btnDim, marginRight: 8 }} onClick={() => triggerRulesRescore(true)}>Score not-yet-analysed</button>
          <button data-testid="rescore-rules-btn" style={{ ...styles.btnDim, marginRight: 8 }} onClick={() => triggerRulesRescore(false)}>Re-score rules only</button>
          <button data-testid="rescore-all-btn" style={styles.btn} onClick={triggerRescore}>Re-score all</button>
        </div>
      </div>
      <RescoreJobPanel styles={styles} startedJobId={jobId} onFinished={reload} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {tiles.map(([k, v, tid]) => (
          <div key={tid} data-testid={tid} style={styles.card}>
            <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{k}</div>
            <div style={{ fontSize: 22, color: "var(--hs-accent)" }}>{v}</div>
          </div>
        ))}
      </div>
      <LlmStatus data={data} styles={styles} onTested={reload} />
      <div style={{ ...styles.card, fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="rescore-last-run">
        current rules: <code data-testid="rules-version">{data.rules_pack_version || "?"}</code>
        {data.rescore_last_run_at ? (
          <> · last re-score: <Time value={data.rescore_last_run_at} /> ({data.rescore_last_kind}) under{" "}
            <code>{data.rescore_last_detector_version}</code>
            {data.detector_version && data.rescore_last_detector_version !== data.detector_version && (
              <span className="hs-text-danger" data-testid="rescore-outdated"> · not yet re-scored with the current rules</span>)}
          </>
        ) : <> · no re-score yet</>}
      </div>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Confirmed · last 6 months</h3>
        {/* Plain HTML bars: an SVG stretched with preserveAspectRatio="none" distorted the axis labels. */}
        <div className="hs-bars" role="img" data-testid="confirmed-chart"
             aria-label={`Confirmed per month: ${data.confirmed_last_6_months_by_month.map(b => `${b.month} ${b.count}`).join(", ")}`}>
          {data.confirmed_last_6_months_by_month.map((b) => (
            <div key={b.month} className="hs-bar-col">
              <span className="hs-bar-value">{b.count}</span>
              <div className="hs-bar-track"><div className="hs-bar" style={{ height: `${Math.round(100 * (b.count / max))}%` }} /></div>
              <span className="hs-bar-label">{b.month.slice(2)}</span>
            </div>
          ))}
        </div>
      </div>
      <SourcesOverview styles={styles} />
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Scanner health</h3>
        <table style={styles.tbl} data-testid="scanner-health-table">
          <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Kind</th><th style={styles.th}>Enabled</th><th style={styles.th}>Items</th><th style={styles.th}>Last run</th><th style={styles.th}>Status</th><th style={styles.th}>Started / ended</th><th style={styles.th}>Fetched</th><th style={styles.th}>New / changed</th><th style={styles.th}>Errors</th></tr></thead>
          <tbody>{data.scanner_health.map(s => (
            <tr key={s.source_id} style={s.alert ? { background: "var(--hs-danger-subtle)" } : undefined} data-alert={s.alert ? "true" : undefined}>
              <td style={styles.td}>{s.name}</td><td style={styles.td}>{s.kind}</td>
              <td style={styles.td}>{s.enabled ? "yes" : "no"}</td><td style={styles.td}>{s.items_found_last_run}</td>
              <td style={styles.td}><Time value={s.last_run_at} /></td>
              <td style={styles.td} data-testid={`health-status-${s.source_id}`}>
                {s.last_status === "running" ? (s.lease_active ? "running ⚠" : "abandoned ⚠") : `${s.last_status}${s.alert ? " ⚠" : ""}`}</td>
              <td style={{ ...styles.td, fontSize: 12 }}>{s.last_run ? <><Time value={s.last_run.started_at} /> → {s.last_run.finished_at ? <Time value={s.last_run.finished_at} /> : "…"}</> : "—"}</td>
              <td style={styles.td}>{s.last_run ? s.last_run.fetches : "—"}</td>
              <td style={styles.td}>{s.last_run ? `${s.last_run.items_new ?? 0} / ${s.last_run.items_changed ?? 0}` : "—"}</td>
              <td style={{ ...styles.td, color: "var(--hs-danger)", fontSize: 12 }}>{s.last_run && s.last_run.skipped > 0 ? `skipped ${s.last_run.skipped} (${s.last_run.skipped_timeouts || 0} timeouts) · ` : ""}{s.last_run && (s.last_run.errors || []).length ? s.last_run.errors[0] : ""}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Review() {
  const [tab, setTab] = useState("queue");
  return (
    <div>
      <div role="tablist" aria-label="Review" style={{ ...styles.row, marginBottom: 12 }}>
        <button type="button" role="tab" aria-selected={tab === "queue"} data-testid="review-tab-queue"
                style={tab === "queue" ? styles.btn : styles.btnDim} onClick={() => setTab("queue")}>Queue</button>
        <button type="button" role="tab" aria-selected={tab === "mine"} data-testid="review-tab-mine"
                style={tab === "mine" ? styles.btn : styles.btnDim} onClick={() => setTab("mine")}>My recent reviews</button>
      </div>
      {tab === "queue" ? <ReviewQueue styles={styles} /> : <><h2>My recent reviews</h2><MyReviews styles={styles} /></>}
    </div>
  );
}

function Items() {
  return <ItemsPage styles={styles} tierColors={TIER_COLORS} Tag={Tag} />;
}

function DisclosuresPanel({ itemId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "email", contact: "", notes: "" });
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.itemDisclosures(itemId);
      setRows(r.data.disclosures || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    await api.createDisclosure(itemId, form);
    setOpen(false); setForm({ channel: "email", contact: "", notes: "" }); load();
  };
  const patch = async (id, outcome) => {
    await api.patchDisclosure(id, { outcome }); load();
  };
  return (
    <div style={styles.card} data-testid="disclosures-panel">
      <div style={{ display: "flex", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>Disclosures ({rows.length})</h3>
        <button data-testid="new-disclosure-btn" style={{ ...styles.btnDim, marginLeft: "auto" }}
                onClick={() => setOpen(!open)}>{open ? "Cancel" : "New"}</button>
      </div>
      {open && (
        <div data-testid="new-disclosure-form" style={{ marginTop: 10 }}>
          <select data-testid="disclosure-channel" style={styles.select} value={form.channel}
                  onChange={e => setForm({ ...form, channel: e.target.value })}>
            {["email", "github_issue", "registry_report", "other"].map(c => <option key={c}>{c}</option>)}
          </select>
          {" "}
          <input data-testid="disclosure-contact" style={styles.input} placeholder="contact"
                 value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} />
          {" "}
          <input data-testid="disclosure-notes" style={{ ...styles.input, width: 260 }}
                 placeholder="notes" value={form.notes}
                 onChange={e => setForm({ ...form, notes: e.target.value })} />
          {" "}
          <button data-testid="disclosure-save" style={styles.btn} onClick={create}>Save</button>
        </div>
      )}
      {rows.map(d => (
        <div key={d.id} data-testid={`disclosure-row-${d.id.slice(0, 8)}`}
             style={{ marginTop: 10, borderTop: "1px solid var(--hs-border)", paddingTop: 8 }}>
          <div style={{ fontSize: 12 }}>
            <code>{d.channel}</code> · {d.contact} · <Tag color={STATUS_COLORS[d.outcome] || "neutral"}>{d.outcome}</Tag>
            {" · "}<span style={{ color: "var(--hs-text-muted)" }}><Time value={d.contacted_at} /></span>
          </div>
          {d.notes && <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 4 }}>{d.notes}</div>}
          <div style={{ marginTop: 6 }}>
            <select data-testid={`disclosure-outcome-${d.id.slice(0, 8)}`} style={styles.select}
                    value={d.outcome} onChange={e => patch(d.id, e.target.value)}>
              {["pending", "acknowledged", "removed", "disputed", "no_response"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferencesPanel({ itemId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ public_reference_at: "", url: "", kind: "cve", notes: "" });
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.itemReferences(itemId);
      setRows(r.data.references || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.public_reference_at || !form.url) return;
    await api.createReference(itemId, form);
    setForm({ public_reference_at: "", url: "", kind: "cve", notes: "" }); load();
  };
  const remove = async (id) => { await api.deleteReference(id); load(); };
  return (
    <div style={styles.card} data-testid="references-panel">
      <h3 style={{ marginTop: 0 }}>Public references ({rows.length})</h3>
      {error && <ErrorNote message={error} onRetry={load} />}
      {rows.map(r => (
        <div key={r.id} data-testid={`ref-row-${r.id.slice(0, 8)}`}
             style={{ fontSize: 12, marginBottom: 6, display: "flex", gap: 8, alignItems: "center" }}>
          <code>{r.kind}</code>
          <span>{r.public_reference_at?.slice(0, 10)}</span>
          <span style={{ fontFamily: "var(--hs-font-mono)", color: "var(--hs-text-muted)", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{r.url}</span>
          <button data-testid={`ref-delete-${r.id.slice(0, 8)}`} style={styles.btnDim} onClick={() => remove(r.id)}>Delete</button>
        </div>
      ))}
      <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <input data-testid="ref-pub-at" type="date" style={styles.input}
               value={form.public_reference_at}
               onChange={e => setForm({ ...form, public_reference_at: e.target.value })} />
        <input data-testid="ref-url" style={{ ...styles.input, flex: 1, minWidth: 220 }} placeholder="url"
               value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
        <select data-testid="ref-kind" style={styles.select} value={form.kind}
                onChange={e => setForm({ ...form, kind: e.target.value })}>
          {["cve", "advisory", "news", "registry_takedown", "other"].map(k => <option key={k}>{k}</option>)}
        </select>
        <button data-testid="ref-save" style={styles.btn} onClick={create}>Add</button>
      </div>
    </div>
  );
}

// "What this is" (backend core/summaries.py): written by the model in the item's normal model call, or a template
// from the item's facts. The public Check page shows `preview.summary` (after the neutrality filter).
function ItemSummary({ item, preview }) {
  const s = item.summary || {};
  if (!s.text && !preview?.summary) return null;
  const filtered = s.source === "model" && preview && preview.summary_source !== "model";
  return (
    <div style={styles.card} data-testid="item-summary">
      <h3 style={{ marginTop: 0 }}>What this is</h3>
      <p data-testid="item-summary-text" style={{ margin: 0 }}>{preview?.summary || s.text}</p>
      <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 6 }} data-testid="item-summary-meta">
        {preview?.summary_source === "model" ? "Written by the model" : "Standard text from the item's facts"}
        {s.prompt_version ? ` · prompt ${s.prompt_version}` : ""}
        {s.content_hash ? ` · content ${s.content_hash.slice(0, 8)}` : ""}
        {s.created_at ? <> · <Time value={s.created_at} /></> : ""}
      </div>
      {filtered && (
        <div style={{ fontSize: 12, marginTop: 6 }} data-testid="item-summary-filtered">
          The model's text is not shown publicly (it failed the neutrality check): <q>{s.text}</q>
        </div>
      )}
    </div>
  );
}

function ItemDetail() {
  const { id } = useParams();
  const loadItemDetail = useCallback(() => api.itemDetail(id), [id]);
  const { data, error, loading, reload } = useApi(loadItemDetail, [id]);
  // Bumped when the AIs are re-asked, so the verdict list reloads its latest entries and attempts.
  const [aiRev, setAiRev] = useState(0);
  if (!data && !error && loading) return <div>Loading…</div>;
  if (error) return <ErrorNote message={error} onRetry={reload} />;
  return (
    <div data-testid="item-detail-page">
      <h2>{data.item.identifier}</h2>
      <div style={styles.card}>Current tier: <Tag color={TIER_COLORS[data.item.current_tier] || "neutral"}>{data.item.current_tier}</Tag>
        {data.item.appeal_upheld_at && <> · <Tag color="danger">appeal upheld</Tag></>}
        {data.item.public_hidden && <> · <Tag color="warning">hidden from public</Tag></>}
        {(data.item.capabilities || []).length > 0 && <div style={{ marginTop: 8 }}>Broad access: <CapabilityBadges capabilities={data.item.capabilities} /></div>}
        <CautionBadges badges={data.item.caution} details={data.public_view?.caution_details} />
      </div>
      <ItemSummary item={data.item} preview={data.public_view} />
      <EvidencePanel itemId={id} styles={styles} onChanged={reload} refresh={aiRev} />
      {["mcp_server", "package"].includes(data.item.type) && <CodeEvidencePanel itemId={id} styles={styles} />}
      <ProfilePanel itemId={id} itemType={data.item.type} styles={styles} />
      <SecondOpinionPanel itemId={id} styles={styles} onChanged={() => { reload(); setAiRev((n) => n + 1); }} />
      <UpstreamReportPanel itemId={id} styles={styles} />
      <ItemModeration item={data.item} snapshotsCount={data.snapshots.length} styles={styles} onChanged={reload} />
      <DisclosuresPanel itemId={id} />
      <ReferencesPanel itemId={id} />
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Snapshots ({data.snapshots.length})</h3>
        {data.snapshots.map(s => (
          <div key={s.id} style={{ fontSize: 12, marginBottom: 6 }}>
            <code>{s.content_hash?.slice(0, 16)}</code> · {s.size_bytes}B · <Time value={s.fetched_at} />
          </div>
        ))}
      </div>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Verdicts ({data.verdicts.length})</h3>
        {data.verdicts.map((v, i) => (
          <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>
            <code>{v.source}</code> · {v.label} · {(v.techniques || []).join(", ")} · <Time value={v.created_at} />
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_SOURCE = { name: "", kind: "mcp_official_registry", url: "", cadence_sec: 3600, max_items_per_run: 200, notes: "",
  max_requests_per_run: "", max_requests_per_day: "", llm_daily_share: "", priority: "" };
const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

function Sources() {
  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [kindInfo, setKindInfo] = useState({ defaults: {}, noUrl: [], cap: 0 });
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState(EMPTY_SOURCE);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const load = useCallback(() => api.sources().then(r => {
    setRows(r.data.sources); setKinds(r.data.kinds || []); setInfo(r.data.scheduler);
    setKindInfo({ defaults: r.data.kind_defaults || {}, noUrl: r.data.no_url_kinds || [], cap: r.data.llm_daily_cap_aud || 0 });
  }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobId) return;
    const t = setInterval(async () => {
      try {
        const r = await api.rescoreStatus(jobId);
        setJobStatus(r.data);
        if (r.data.status === "done" || r.data.status === "failed") clearInterval(t);
      } catch (_) { clearInterval(t); }
    }, 500);
    return () => clearInterval(t);
  }, [jobId]);
  const guard = async (fn, ok) => {
    try { await fn(); if (ok) setMsg(ok); await load(); } catch (ex) { setMsg("Error: " + ex.message); }
  };
  const toggle = (s) => guard(() => api.patchSource(s.id, { enabled: !s.enabled }));
  const run = (s) => { setMsg(`Running ${s.name}…`); return guard(async () => {
    const r = await api.runSource(s.id);
    if (r.data?.queued) {
      setMsg(`Queued ${s.name} for the worker (${(r.data.request_id || r.data.result?.request_id || "request").slice(0, 8)}).`);
      return;
    }
    setMsg(`Ran ${s.name}: ${r.data.result.status}, fetched ${r.data.result.fetches}, new ${r.data.result.items_new}.`);
  }); };
  const create = (e) => {
    e.preventDefault();
    return guard(async () => {
      const body = { ...form, cadence_sec: Number(form.cadence_sec), max_items_per_run: Number(form.max_items_per_run),
        max_requests_per_run: numOrNull(form.max_requests_per_run), max_requests_per_day: numOrNull(form.max_requests_per_day),
        llm_daily_share: numOrNull(form.llm_daily_share) == null ? null : numOrNull(form.llm_daily_share) / 100, priority: numOrNull(form.priority) };
      if (kindInfo.noUrl.includes(form.kind)) delete body.url;
      await api.createSource(body);
      setForm(EMPTY_SOURCE);
    }, "Source created (disabled — enable it when ready).");
  };
  const startEdit = (s) => {
    setEditId(s.id);
    setEdit({ url: s.url || "", cadence_sec: s.cadence_sec ?? 0, max_items_per_run: s.max_items_per_run ?? 200, notes: s.notes || "",
      max_requests_per_run: s.max_requests_per_run ?? "", max_requests_per_day: s.max_requests_per_day ?? "",
      llm_daily_share: s.llm_daily_share == null ? "" : Math.round(s.llm_daily_share * 100) });
  };
  const saveEdit = (s) => guard(async () => {
    const patch = { cadence_sec: Number(edit.cadence_sec), max_items_per_run: Number(edit.max_items_per_run), notes: edit.notes,
      max_requests_per_run: numOrNull(edit.max_requests_per_run), max_requests_per_day: numOrNull(edit.max_requests_per_day),
      llm_daily_share: numOrNull(edit.llm_daily_share) == null ? null : numOrNull(edit.llm_daily_share) / 100 };
    if (!kindInfo.noUrl.includes(s.kind)) patch.url = edit.url;
    await api.patchSource(s.id, patch);
    setEditId(null);
  }, `Saved ${s.name}.`);
  const del = (s) => {
    if (!window.confirm(`Delete source "${s.name}"? Its items are kept.`)) return;
    return guard(() => api.deleteSource(s.id, false), `Deleted ${s.name}.`);
  };
  const delDemo = () => {
    if (!window.confirm("Delete ALL demo sources and their synthetic items?")) return;
    return guard(async () => {
      const r = await api.deleteDemoSources();
      setMsg(`Deleted ${r.data.deleted.length} demo source(s) and ${r.data.items_purged} item(s).`);
    });
  };
  const rescoreSrc = async (s) => {
    const r = await api.rescore({ source_id: s.id });
    if (r.data?.queued) {
      setMsg(`Queued source re-score for ${s.name} (${(r.data.request_id || r.data.job_id || "request").slice(0, 8)}). Check Scanner health in a minute.`);
      return;
    }
    setJobId(r.data.job_id); setJobStatus({ status: "queued", processed: 0, total: 0 });
  };
  const refetch = (s) => {
    if (!window.confirm(`Re-fetch ${s.content_missing_count || "all flagged"} item(s) with missing content from the registry? ` +
      "Content that is unchanged is restored without creating a new snapshot.")) return undefined;
    setMsg(`Refetching missing content for ${s.name}…`);
    return guard(async () => {
      const r = await api.refetchMissing(s.id);
      if (r.data?.queued) {
        setMsg(`Queued refetch for ${s.name} (${(r.data.request_id || "request").slice(0, 8)}). Check Scanner health in a minute.`);
        return;
      }
      const x = r.data.result;
      setMsg(`Refetch ${s.name}: ${x.status} — restored ${x.restored ?? 0}, changed (new snapshot) ${x.items_changed ?? 0}, ` +
        `not found upstream ${x.not_found ?? 0}, ${x.fetches} request(s), ${x.missing_remaining ?? 0} still missing` +
        `${(x.errors || []).length ? `; error: ${x.errors[0]}` : ""}.`);
    });
  };
  const [forceEst, setForceEst] = useState(null);
  const askForce = (s) => guard(async () => {
    const r = await api.rescoreEstimate({ source_id: s.id, force: true });
    setForceEst({ source: s, est: r.data });
  });
  const runForce = async () => {
    const r = await api.rescore({ source_id: forceEst.source.id, force: true });
    setForceEst(null);
    if (r.data?.queued) {
      setMsg(`Queued forced re-score for ${forceEst.source.name} (${(r.data.request_id || r.data.job_id || "request").slice(0, 8)}). Check Scanner health in a minute.`);
      return;
    }
    setJobId(r.data.job_id); setJobStatus({ status: "queued", processed: 0, total: 0 });
  };
  const hasDemo = rows.some(s => s.is_demo);
  return (
    <div data-testid="sources-page">
      <h2>Sources</h2>
      {info && (
        <div style={{ ...styles.card, fontSize: 12 }} data-testid="scheduler-info">
          Scheduled scanning is <strong style={{ color: info.scans_enabled ? "var(--hs-safe)" : "var(--hs-danger)" }}>
            {info.scans_enabled ? "ON" : "OFF"}</strong>
          {" "}(SCANNERS_ENABLED={String(info.SCANNERS_ENABLED)}, OFFLINE_MODE={String(info.OFFLINE_MODE)}).
          {" "}Scheduled scans need SCANNERS_ENABLED=true and OFFLINE_MODE=false. Minimum cadence {info.min_cadence_sec}s.
        </div>
      )}
      {msg && <div style={{ marginBottom: 10, color: "var(--hs-text-muted)" }} data-testid="sources-msg">{msg}</div>}
      {jobStatus && (
        <div style={styles.card} data-testid="sources-rescore-toast">
          Re-score <code>{jobId?.slice(0, 8)}</code>: <strong>{jobStatus.status}</strong>
          {" · "}{jobStatus.processed}/{jobStatus.total}
          {" · skipped "}{jobStatus.skipped ?? 0}
          {jobStatus.missing_content > 0 && <> · <span style={{ color: "var(--hs-danger)" }}>{jobStatus.missing_content} skipped: content missing</span></>}
          {jobStatus.force && <> · <strong>forced</strong></>}
          {jobStatus.llm && (
            <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="sources-rescore-llm">
              model step: {jobStatus.llm.ok} ok · {jobStatus.llm.error} error · {jobStatus.llm.cap} stopped by cap
              {" · "}why: {Object.entries(jobStatus.llm.reasons || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "—"}
            </div>
          )}
        </div>
      )}
      {forceEst && (
        <div style={styles.card} data-testid="sources-force-estimate">
          <strong>Force re-score {forceEst.source.name}</strong>: {forceEst.est.would_call_llm} of {forceEst.est.total} snapshot(s)
          would go to the model{forceEst.est.missing_content > 0 && <span style={{ color: "var(--hs-danger)" }}> ({forceEst.est.missing_content} skipped: content missing)</span>}
          {forceEst.est.llm_available
            ? <> (<code>{forceEst.est.model}</code>), est. <strong>A${forceEst.est.estimated_cost_aud}</strong>; daily cap A${forceEst.est.daily_cap_aud},
              {" "}A${forceEst.est.cap_remaining_aud} left{forceEst.est.would_exceed_cap && <span style={{ color: "var(--hs-danger)" }}> — exceeds the remaining cap, the run stops at the cap</span>}.</>
            : <> — no Anthropic key configured, the free mock would be used (A$0).</>}
          {" "}<button data-testid="sources-force-run" style={styles.btn} onClick={runForce}>Run forced re-score</button>{" "}
          <button style={styles.btnDim} onClick={() => setForceEst(null)}>Cancel</button>
        </div>
      )}
      <form onSubmit={create} style={styles.card} data-testid="source-create-form">
        <h3 style={{ marginTop: 0 }}>Add source</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input data-testid="source-name" required style={styles.input} placeholder="name (a-z 0-9 . _ -)" value={form.name}
                 onChange={e => setForm({ ...form, name: e.target.value })} />
          <select data-testid="source-kind" style={styles.select} value={form.kind}
                  onChange={e => setForm({ ...form, kind: e.target.value })}>
            {(kinds.length ? kinds : [form.kind]).map(k => <option key={k}>{k}</option>)}
          </select>
          {!kindInfo.noUrl.includes(form.kind) && (
            <input data-testid="source-url" style={{ ...styles.input, width: 360 }} placeholder="https:// URL (blank = official default)"
                   value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />)}
          <input data-testid="source-cadence" type="number" style={{ ...styles.input, width: 110 }} title="cadence_sec (0 = manual only, min 300)"
                 value={form.cadence_sec} onChange={e => setForm({ ...form, cadence_sec: e.target.value })} />
          <input data-testid="source-max" type="number" style={{ ...styles.input, width: 110 }} title="max items per run"
                 value={form.max_items_per_run} onChange={e => setForm({ ...form, max_items_per_run: e.target.value })} />
          <input data-testid="source-max-run" type="number" style={{ ...styles.input, width: 120 }} title="max HTTP requests per run (blank = kind default / unlimited)"
                 placeholder={String(kindInfo.defaults[form.kind]?.max_requests_per_run ?? "req/run")} value={form.max_requests_per_run}
                 onChange={e => setForm({ ...form, max_requests_per_run: e.target.value })} />
          <input data-testid="source-max-day" type="number" style={{ ...styles.input, width: 120 }} title="max HTTP requests per day (blank = kind default / unlimited)"
                 placeholder={String(kindInfo.defaults[form.kind]?.max_requests_per_day ?? "req/day")} value={form.max_requests_per_day}
                 onChange={e => setForm({ ...form, max_requests_per_day: e.target.value })} />
          <input data-testid="source-llm-share" type="number" min="0" max="100" style={{ ...styles.input, width: 120 }}
                 title={`share of the daily LLM budget (A$${kindInfo.cap}) this source may spend, in % (blank = kind default / no share)`}
                 placeholder={kindInfo.defaults[form.kind]?.llm_daily_share != null ? `${kindInfo.defaults[form.kind].llm_daily_share * 100}% LLM` : "% LLM share"}
                 value={form.llm_daily_share} onChange={e => setForm({ ...form, llm_daily_share: e.target.value })} />
          <input data-testid="source-notes" style={{ ...styles.input, width: 240 }} placeholder="notes" value={form.notes}
                 onChange={e => setForm({ ...form, notes: e.target.value })} />
          <button data-testid="source-create" type="submit" style={styles.btn}>Create</button>
        </div>
      </form>
      {hasDemo && (
        <div style={{ marginBottom: 10 }}>
          <button data-testid="delete-demo-btn" style={styles.btnDim} onClick={delDemo}>Delete demo sources</button>
        </div>
      )}
      <table style={styles.tbl}>
        <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Kind</th><th style={styles.th}>URL</th><th style={styles.th}>Cadence / max</th><th style={styles.th}>Enabled</th><th style={styles.th}>Items</th><th style={styles.th}>Last run</th><th style={styles.th}></th></tr></thead>
        <tbody>{rows.map(s => (
          <tr key={s.id}>
            <td style={styles.td}>{s.name}{s.is_demo && <> <Tag color="warning">demo</Tag></>}
              {s.notes && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{s.notes}</div>}</td>
            <td style={styles.td}>{s.kind}</td>
            {editId === s.id ? (
              <>
                <td style={styles.td}>{!kindInfo.noUrl.includes(s.kind) && <input style={{ ...styles.input, width: 260 }} value={edit.url} onChange={e => setEdit({ ...edit, url: e.target.value })} />}
                  <input style={{ ...styles.input, width: 260, marginTop: 4 }} placeholder="notes" value={edit.notes} onChange={e => setEdit({ ...edit, notes: e.target.value })} /></td>
                <td style={styles.td}>
                  <input type="number" style={{ ...styles.input, width: 90 }} value={edit.cadence_sec} onChange={e => setEdit({ ...edit, cadence_sec: e.target.value })} />
                  {" "}<input type="number" style={{ ...styles.input, width: 80 }} value={edit.max_items_per_run} onChange={e => setEdit({ ...edit, max_items_per_run: e.target.value })} />
                  <div style={{ marginTop: 4 }}>
                    <input type="number" title="requests per run" placeholder="req/run" style={{ ...styles.input, width: 80 }} value={edit.max_requests_per_run} onChange={e => setEdit({ ...edit, max_requests_per_run: e.target.value })} />{" "}
                    <input type="number" title="requests per day" placeholder="req/day" style={{ ...styles.input, width: 80 }} value={edit.max_requests_per_day} onChange={e => setEdit({ ...edit, max_requests_per_day: e.target.value })} />{" "}
                    <input type="number" title="LLM share %" placeholder="LLM %" style={{ ...styles.input, width: 70 }} value={edit.llm_daily_share} onChange={e => setEdit({ ...edit, llm_daily_share: e.target.value })} />
                  </div>
                </td>
              </>
            ) : (
              <>
                <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>{s.url}</td>
                <td style={styles.td}>{s.cadence_sec}s / {s.max_items_per_run ?? "—"}
                  {(s.max_requests_per_run != null || s.max_requests_per_day != null || s.llm_daily_share != null) && (
                    <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }} data-testid={`caps-${s.name}`}>
                      req {s.max_requests_per_run ?? "∞"}/run · {s.requests_today ?? 0}/{s.max_requests_per_day ?? "∞"} today
                      {s.llm_daily_share != null && <> · LLM {Math.round(s.llm_daily_share * 100)}% (A${(s.llm_spend_today_aud ?? 0).toFixed(3)} today)</>}
                    </div>)}
                </td>
              </>
            )}
            <td style={styles.td}>
              <button data-testid={`toggle-${s.name}`} style={styles.btnDim} onClick={() => toggle(s)}>{s.enabled ? "yes" : "no"}</button>
            </td>
            <td style={styles.td}>{s.items_count}
              {s.content_missing_count > 0 && <div style={{ fontSize: 12, color: "var(--hs-danger)" }} data-testid={`missing-${s.name}`}>{s.content_missing_count} missing content</div>}
            </td>
            <td style={styles.td}><RunSummary run={s.last_run} /></td>
            <td style={styles.td}>
              {editId === s.id ? (
                <>
                  <button data-testid={`save-${s.name}`} style={styles.btnDim} onClick={() => saveEdit(s)}>Save</button>{" "}
                  <button style={styles.btnDim} onClick={() => setEditId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <button data-testid={`run-${s.name}`} style={styles.btnDim} onClick={() => run(s)}>Run now</button>{" "}
                  <button data-testid={`rescore-${s.name}`} style={styles.btnDim} onClick={() => rescoreSrc(s)}>Re-score</button>{" "}
                  <button data-testid={`force-${s.name}`} style={styles.btnDim} onClick={() => askForce(s)}>Force…</button>{" "}
                  {s.kind === "mcp_official_registry" && (
                    <><button data-testid={`refetch-${s.name}`} style={styles.btnDim} onClick={() => refetch(s)}>Refetch missing content</button>{" "}</>
                  )}
                  <button data-testid={`edit-${s.name}`} style={styles.btnDim} onClick={() => startEdit(s)}>Edit</button>{" "}
                  <button data-testid={`delete-${s.name}`} style={styles.btnDim} onClick={() => del(s)}>Delete</button>
                </>
              )}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Disclosures() {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.listDisclosures(outcome ? `outcome=${outcome}` : "");
      setRows(r.data.disclosures || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [outcome]);
  useEffect(() => { load(); }, [load]);
  const patch = async (d, newO) => { await api.patchDisclosure(d.id, { outcome: newO }); load(); };
  return (
    <div data-testid="disclosures-page">
      <h2>Disclosures</h2>
      {error && <ErrorNote message={error} onRetry={load} />}
      <div style={styles.row}>
        <select data-testid="disclosures-outcome-filter" style={styles.select} value={outcome}
                onChange={e => setOutcome(e.target.value)}>
          <option value="">all outcomes</option>
          {["pending", "acknowledged", "removed", "disputed", "no_response"].map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
      <table style={styles.tbl}>
        <thead><tr><th style={styles.th}>When</th><th style={styles.th}>Channel</th><th style={styles.th}>Contact</th><th style={styles.th}>Item</th><th style={styles.th}>Outcome</th></tr></thead>
        <tbody>{rows.map(d => (
          <tr key={d.id} data-testid={`disc-row-${d.id.slice(0, 8)}`}>
            <td style={styles.td}><Time value={d.contacted_at} /></td>
            <td style={styles.td}>{d.channel}</td>
            <td style={styles.td}>{d.contact}</td>
            <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>
              <Link style={{ color: "var(--hs-accent)" }} to={`/admin/items/${d.item_id}`}>{d.item_id.slice(0, 8)}</Link>
            </td>
            <td style={styles.td}>
              <select style={styles.select} value={d.outcome}
                      onChange={e => patch(d, e.target.value)}>
                {["pending", "acknowledged", "removed", "disputed", "no_response"].map(o => <option key={o}>{o}</option>)}
              </select>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Appeals() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [view, setView] = useState("active");
  const [error, setError] = useState(null);
  const selection = useSelection();
  const load = useCallback(async () => {
    try {
      const r = await api.listAppeals(`view=${view}${status ? `&status=${status}` : ""}`);
      setRows(r.data.appeals || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [status, view]);
  useEffect(() => { load(); }, [load]);
  const patch = async (a, s) => { await api.patchAppeal(a.id, { status: s, resolution_notes: "" }); load(); };
  return (
    <div data-testid="appeals-page">
      <h2>Appeals</h2>
      {error && <ErrorNote message={error} onRetry={load} />}
      <div style={styles.row}>
        <select data-testid="appeals-status-filter" style={styles.select} value={status}
                onChange={e => setStatus(e.target.value)}>
          <option value="">all</option>
          <option value="open">open</option>
          <option value="upheld">upheld</option>
          <option value="rejected">rejected</option>
        </select>
      </div>
      <ModerationBar kind="appeals" view={view} setView={setView} selection={selection} styles={styles} onDone={load} />
      <table style={styles.tbl}>
        <thead><tr><th style={styles.th}></th><th style={styles.th}>When</th><th style={styles.th}>Email</th><th style={styles.th}>Target</th><th style={styles.th}>Status</th><th style={styles.th}>Message</th><th></th></tr></thead>
        <tbody>{rows.map(a => (
          <tr key={a.id} data-testid={`appeal-row-${a.id.slice(0, 8)}`}>
            <td style={styles.td}><RowCheck id={a.id} selection={selection} label={a.contact_email} /></td>
            <td style={styles.td}><Time value={a.created_at} /></td>
            <td style={styles.td}>{a.contact_email}</td>
            <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>{a.item_type}·{a.item_identifier?.slice(0, 32)}</td>
            <td style={styles.td}><Tag color={STATUS_COLORS[a.status] || "neutral"}>{a.status}</Tag></td>
            <td style={{ ...styles.td, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.message}</td>
            <td style={styles.td}>
              {a.status === "open" && (
                <>
                  <button data-testid={`uphold-${a.id.slice(0, 8)}`} style={styles.btnDim} onClick={() => patch(a, "upheld")}>Uphold</button>
                  {" "}
                  <button data-testid={`reject-${a.id.slice(0, 8)}`} style={styles.btnDim} onClick={() => patch(a, "rejected")}>Reject</button>
                </>
              )}
              {" "}<BlockButton kind="appeals" id={a.id} styles={styles} onDone={load} />
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Submissions() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("active");
  const [error, setError] = useState(null);
  const selection = useSelection();
  const load = useCallback(async () => {
    try {
      const r = await api.listSubmissions(`view=${view}${status ? `&status=${status}` : ""}`);
      setRows(r.data.submissions || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [status, view]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    api.getSubmission(sel).then(r => setDetail(r.data.submission)).catch((err) => setDetail({ error: err }));
  }, [sel]);
  const patch = async (s, newS) => { await api.patchSubmission(s.id, { status: newS, notes: "" }); load(); if (sel === s.id) setSel(s.id); };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }} data-testid="submissions-page">
      <div>
        <h2>Submissions</h2>
        {error && <ErrorNote message={error} onRetry={load} />}
        <div style={styles.row}>
          <select data-testid="submissions-status-filter" style={styles.select} value={status}
                  onChange={e => setStatus(e.target.value)}>
            <option value="">all</option>
            {["new", "in_review", "accepted", "rejected"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <ModerationBar kind="submissions" view={view} setView={setView} selection={selection} styles={styles} onDone={load} />
        <table style={styles.tbl}>
          <thead><tr><th style={styles.th}></th><th style={styles.th}>When</th><th style={styles.th}>Type</th><th style={styles.th}>Identifier</th><th style={styles.th}>Status</th><th></th></tr></thead>
          <tbody>{rows.map(s => (
            <tr key={s.id} data-testid={`sub-row-${s.id.slice(0, 8)}`}
                onClick={() => setSel(s.id)} style={{ cursor: "pointer" }}>
              <td style={styles.td} onClick={(e) => e.stopPropagation()}><RowCheck id={s.id} selection={selection} label={s.identifier} /></td>
              <td style={styles.td}><Time value={s.created_at} /></td>
              <td style={styles.td}>{s.item_type}</td>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>{s.identifier}</td>
              <td style={styles.td}><Tag color={STATUS_COLORS[s.status] || "neutral"}>{s.status}</Tag></td>
              <td style={styles.td}>
                {s.status !== "accepted" && (
                  <button data-testid={`accept-${s.id.slice(0, 8)}`} style={styles.btnDim} onClick={e => { e.stopPropagation(); patch(s, "accepted"); }}>Accept</button>
                )}
                {" "}
                {s.status !== "rejected" && (
                  <button data-testid={`reject-${s.id.slice(0, 8)}`} style={styles.btnDim} onClick={e => { e.stopPropagation(); patch(s, "rejected"); }}>Reject</button>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div style={styles.card} data-testid="submission-detail-card">
        <h3 style={{ marginTop: 0 }}>Evidence (escaped)</h3>
        {!detail && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>Select a submission.</div>}
        {detail && detail.error ? <ErrorNote message={detail.error} onRetry={() => api.getSubmission(sel).then(r => setDetail(r.data.submission)).catch((err) => setDetail({ error: err }))} /> : <RawTextViewer text={detail?.evidence_text || ""} matches={[]} />}
      </div>
    </div>
  );
}

const WAITLIST_STATUSES = ["new", "contacted", "invited", "archived"];
const WAITLIST_TONE = { new: "info", contacted: "warning", invited: "safe", archived: "neutral" };

// Early-access sign-ups from /early-access. Everything here is visitor-typed text: rendered as text only.
function Waitlist() {
  const [data, setData] = useState({ entries: [], total: 0, counts: {} });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");
  const [view, setView] = useState("active");
  const selection = useSelection();
  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set("view", view);
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    return api.waitlist(p.toString()).then((r) => setData(r.data)).catch((ex) => setMsg("Error: " + ex.message));
  }, [status, q, view]);
  useEffect(() => { load(); }, [load]);
  const change = async (e, next) => {
    try { await api.patchWaitlist(e.id, { status: next }); setMsg(`${e.email}: ${next}`); load(); }
    catch (ex) { setMsg("Error: " + ex.message); }
  };
  return (
    <div data-testid="waitlist-page">
      <h2>Waitlist</h2>
      <div style={styles.row}>
        <label className="hs-label" htmlFor="wl-search" style={{ marginBottom: 0 }}>Search</label>
        <input id="wl-search" data-testid="waitlist-search" style={styles.input} value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="hs-label" htmlFor="wl-status" style={{ marginBottom: 0 }}>Status</label>
        <select id="wl-status" data-testid="waitlist-status-filter" style={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">all ({Object.values(data.counts || {}).reduce((a, b) => a + b, 0)})</option>
          {WAITLIST_STATUSES.map((s) => <option key={s} value={s}>{s} ({(data.counts || {})[s] || 0})</option>)}
        </select>
      </div>
      <ModerationBar kind="waitlist" view={view} setView={setView} selection={selection} styles={styles} onDone={load} />
      {msg && <div style={{ color: "var(--hs-text-muted)", marginBottom: 8 }} data-testid="waitlist-msg">{msg}</div>}
      <div style={styles.card}>
        <table style={styles.tbl}>
          <thead><tr><th style={styles.th}></th><th style={styles.th}>Signed up</th><th style={styles.th}>Email</th><th style={styles.th}>Company</th>
            <th style={styles.th}>Role</th><th style={styles.th}>MCP servers they use</th><th style={styles.th}>Status</th></tr></thead>
          <tbody>{data.entries.map((e) => (
            <tr key={e.id} data-testid={`waitlist-row-${e.email}`}>
              <td style={styles.td}><RowCheck id={e.id} selection={selection} label={e.email} /></td>
              <td style={styles.td}><Time value={e.created_at} dateOnly />{e.submissions > 1 && <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{e.submissions} sign-ups</div>}</td>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}>{e.email}</td>
              <td style={styles.td}>{e.company || "—"}</td>
              <td style={styles.td}>{e.role || "—"}</td>
              <td style={{ ...styles.td, whiteSpace: "pre-wrap", maxWidth: 360 }}>{e.mcp_servers || "—"}</td>
              <td style={styles.td}>
                <Tag color={WAITLIST_TONE[e.status] || "neutral"}>{e.status}</Tag>{" "}
                <select aria-label={`status for ${e.email}`} data-testid={`waitlist-set-${e.email}`} style={styles.select}
                        value={e.status} onChange={(ev) => change(e, ev.target.value)}>
                  {WAITLIST_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>))}
          {data.entries.length === 0 && <tr><td style={styles.td} colSpan={7}>No sign-ups.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

// Every server profile: state (draft / published / unpublished) and its live status line.
function ProfilesList() {
  const loadProfiles = useCallback(() => api.listProfiles(), []);
  const { data, error, loading, reload } = useApi(loadProfiles, []);
  const rows = data && Array.isArray(data.profiles) ? data.profiles : [];
  if (!data && !error && loading) return <div>Loading…</div>;
  return (
    <div data-testid="profiles-page">
      <h2>Profiles</h2>
      {error && <ErrorNote message={error} onRetry={reload} />}
      <p style={{ color: "var(--hs-text-muted)" }}>Create a profile from an MCP server's item page. Nothing is public until you publish it.</p>
      <div style={styles.card}>
        <table style={styles.tbl}>
          <thead><tr><th style={styles.th}>Server</th><th style={styles.th}>State</th><th style={styles.th}>Status</th><th style={styles.th}>Updated</th></tr></thead>
          <tbody>{(rows || []).map((p) => (
            <tr key={p.id} data-testid={`profile-row-${p.slug}`}>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}><Link className="hs-link" to={`/admin/items/${p.item_id}`}>{p.name}</Link></td>
              <td style={styles.td}><Tag color={p.state === "published" ? "safe" : "neutral"}>{p.state}</Tag>{p.blocked && <> <Tag color="danger">{p.blocked}</Tag></>}</td>
              <td style={styles.td}>{p.status}</td>
              <td style={styles.td}><Time value={p.updated_at} dateOnly /></td>
            </tr>))}
          {rows && rows.length === 0 && <tr><td style={styles.td} colSpan={4}>No profiles yet.</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

function AdminReports() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [msg, setMsg] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.listReports("limit=200");
      setRows(r.data.reports || []);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    api.getReport(sel).then(r => setDetail(r.data.report)).catch((err) => setDetail({ error: err }));
  }, [sel]);
  const generate = async () => {
    try { const r = await api.generateReport(period); setMsg(`generated ${period}`); setSel(r.data.report.id); load(); }
    catch (e) { setMsg(`error: ${e.message}`); }
  };
  const save = async () => {
    try { await api.patchReport(sel, detail.markdown); setMsg("saved"); load(); }
    catch (e) { setMsg(`error: ${e.message}`); }
  };
  const publish = async () => { try { await api.publishReport(sel); setMsg("published"); const r = await api.getReport(sel); setDetail(r.data.report); load(); } catch (e) { setMsg(`error: ${e.message}`); } };
  const unpublish = async () => { try { await api.unpublishReport(sel); setMsg("unpublished"); const r = await api.getReport(sel); setDetail(r.data.report); load(); } catch (e) { setMsg(`error: ${e.message}`); } };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }} data-testid="admin-reports-page">
      {error && <ErrorNote message={error} onRetry={load} />}
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Reports</h3>
        <div style={{ marginBottom: 10 }}>
          <input data-testid="admin-reports-period" style={styles.input}
                 value={period} onChange={e => setPeriod(e.target.value)} placeholder="YYYY-MM" />
          {" "}
          <button data-testid="admin-reports-generate" style={styles.btn} onClick={generate}>Generate</button>
        </div>
        {rows.map(r => (
          <div key={r.id} data-testid={`report-item-${r.period}`}
               onClick={() => setSel(r.id)}
               style={{ padding: 8, cursor: "pointer",
                        background: sel === r.id ? "var(--hs-border)" : "transparent",
                        marginBottom: 4, borderRadius: 4 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <strong>{r.period}</strong>
              <Tag color={STATUS_COLORS[r.status] || "neutral"}>{r.status}</Tag>
            </div>
            <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{r.summary?.slice(0, 90) || "—"}</div>
          </div>
        ))}
        {!rows.length && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>No reports yet. Generate one above.</div>}
      </div>
      <div>
        {!detail && <div style={styles.card}>Select or generate a report.</div>}
        {detail && detail.error ? <ErrorNote message={detail.error} onRetry={() => api.getReport(sel).then(r => setDetail(r.data.report)).catch((err) => setDetail({ error: err }))} /> : (
        detail && (
          <>
            <div style={styles.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 style={{ margin: 0 }}>Report {detail.period}</h3>
                <Tag color={STATUS_COLORS[detail.status] || "neutral"}>{detail.status}</Tag>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button data-testid="admin-report-save" style={styles.btn} onClick={save}
                          disabled={detail.status !== "draft"}>Save Draft</button>
                  {detail.status === "draft" && (
                    <button data-testid="admin-report-publish" style={styles.btn} onClick={publish}>Publish</button>
                  )}
                  {detail.status === "published" && (
                    <button data-testid="admin-report-unpublish" style={styles.btnDim} onClick={unpublish}>Unpublish</button>
                  )}
                </div>
              </div>
              {msg && <div style={{ marginTop: 8, fontSize: 12, color: "var(--hs-safe)" }}>{msg}</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={styles.card}>
                <h4 style={{ marginTop: 0 }}>Markdown</h4>
                <textarea data-testid="admin-report-markdown"
                          value={detail.markdown || ""} rows={30}
                          disabled={detail.status !== "draft"}
                          onChange={e => setDetail({ ...detail, markdown: e.target.value })}
                          style={{ ...styles.input, width: "100%", fontFamily: "var(--hs-font-mono)", fontSize: 12 }} />
              </div>
              <div style={styles.card}>
                <h4 style={{ marginTop: 0 }}>Preview</h4>
                <div data-testid="admin-report-preview" style={{ fontSize: 13, color: "var(--hs-text)" }}>
                  <ReactMarkdown skipHtml>{detail.markdown || ""}</ReactMarkdown>
                </div>
              </div>
            </div>
          </>
        ))}
      </div>
    </div>
  );
}

function ApiKeys() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", owner_email: "", plan: "free", monthly_quota: "" });
  const [open, setOpen] = useState(false);
  const [just, setJust] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.listApiKeys().then(r => setRows(r.data.api_keys || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    setErr("");
    try {
      const body = { name: form.name, owner_email: form.owner_email, plan: form.plan };
      if (form.monthly_quota) body.monthly_quota = parseInt(form.monthly_quota, 10);
      const r = await api.createApiKey(body);
      setJust(r.data);
      setForm({ name: "", owner_email: "", plan: "free", monthly_quota: "" });
      setOpen(false); load();
    } catch (e) { setErr(e.message); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(just.key); setCopied(true); } catch (_) {}
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"?`)) return;
    await api.revokeApiKey(k.id); load();
  };
  return (
    <div data-testid="admin-api-keys-page">
      <div style={styles.row}>
        <h2 style={{ margin: 0 }}>API keys</h2>
        <button data-testid="new-key-btn" style={{ ...styles.btn, marginLeft: "auto" }}
                onClick={() => setOpen(!open)}>{open ? "Cancel" : "New key"}</button>
      </div>
      {open && (
        <div style={styles.card} data-testid="new-key-form">
          <div style={styles.row}>
            <input data-testid="new-key-name" style={styles.input} placeholder="name"
                   value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input data-testid="new-key-email" style={styles.input} placeholder="owner@email"
                   value={form.owner_email}
                   onChange={e => setForm({ ...form, owner_email: e.target.value })} />
            <select data-testid="new-key-plan" style={styles.select} value={form.plan}
                    onChange={e => setForm({ ...form, plan: e.target.value })}>
              {["free", "builder", "scale", "internal"].map(p => <option key={p}>{p}</option>)}
            </select>
            <input data-testid="new-key-quota" style={styles.input} placeholder="monthly_quota (optional)"
                   value={form.monthly_quota}
                   onChange={e => setForm({ ...form, monthly_quota: e.target.value })} />
            <button data-testid="create-key-submit" style={styles.btn} onClick={submit}>Create</button>
          </div>
          {err && <div style={{ color: "var(--hs-danger)", fontSize: 12 }}>{err}</div>}
        </div>
      )}
      {just && (
        <div style={{ ...styles.card, borderColor: "var(--hs-accent)" }} data-testid="new-key-reveal">
          <div style={{ color: "var(--hs-accent)", fontWeight: 700 }}>
            This key will not be shown again. Copy it now.
          </div>
          <div style={{ fontFamily: "var(--hs-font-mono)", wordBreak: "break-all",
                        background: "var(--hs-code-bg)", padding: 12, marginTop: 10, borderRadius: 4 }}
               data-testid="new-key-value">{just.key}</div>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button data-testid="copy-key-btn" style={styles.btnDim} onClick={copy}>
              {copied ? "Copied ✓" : "Copy to clipboard"}
            </button>
            <button style={styles.btnDim} onClick={() => { setJust(null); setCopied(false); }}>
              Dismiss
            </button>
          </div>
        </div>
      )}
      <table style={styles.tbl}>
        <thead><tr>
          <th style={styles.th}>Name</th><th style={styles.th}>Owner</th>
          <th style={styles.th}>Plan</th><th style={styles.th}>Usage / Quota</th>
          <th style={styles.th}>Status</th><th style={styles.th}>Created</th>
          <th></th>
        </tr></thead>
        <tbody>{rows.map(k => (
          <tr key={k.id} data-testid={`key-row-${k.id.slice(0, 8)}`}>
            <td style={styles.td}>{k.name}</td>
            <td style={styles.td}>{k.owner_email}</td>
            <td style={styles.td}><Tag color="info">{k.plan}</Tag></td>
            <td style={styles.td}>
              {k.current_period_usage}{" / "}
              {k.monthly_quota === null ? "unlimited" : k.monthly_quota}
            </td>
            <td style={styles.td}>
              {k.revoked_at
                ? <Tag color="danger">revoked</Tag>
                : <Tag color="safe">active</Tag>}
            </td>
            <td style={styles.td}><Time value={k.created_at} dateOnly /></td>
            <td style={styles.td}>
              {!k.revoked_at && (
                <button data-testid={`revoke-${k.id.slice(0, 8)}`} style={styles.btnDim}
                        onClick={() => revoke(k)}>Revoke</button>
              )}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SigningKeys() {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.listSigningKeys().then(r => setRows(r.data.keys || []));
  useEffect(() => { load(); }, []);
  const rotate = async () => {
    if (!window.confirm("Rotate the signing key? The new key signs the next v1 response immediately; the previous key stays in the JWKS until it falls out of the 3-key retention window.")) return;
    setBusy(true); setMsg("");
    try {
      const r = await api.rotateSigningKey();
      setMsg(`rotated → ${r.data.kid}`);
      load();
    } catch (e) { setMsg("error: " + e.message); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="signing-keys-page">
      <div style={styles.row}>
        <h2 style={{ margin: 0 }}>Signing keys</h2>
        <div style={{ marginLeft: "auto" }}>
          <button data-testid="rotate-signing-key-btn" style={styles.btn}
                  disabled={busy} onClick={rotate}>
            {busy ? "Rotating…" : "Rotate now"}
          </button>
        </div>
      </div>
      {msg && (
        <div data-testid="signing-key-msg" style={{ ...styles.card, color: msg.startsWith("error") ? "var(--hs-danger)" : "var(--hs-safe)", fontSize: 13 }}>
          {msg}
        </div>
      )}
      <div style={styles.card}>
        <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginBottom: 10 }}>
          Up to <strong>3 keys</strong> are published in the JWKS at
          {" "}<code>{JWKS_URL}</code>. The oldest retired key is
          purged on each rotation.
        </div>
        <table style={styles.tbl} data-testid="signing-keys-table">
          <thead><tr>
            <th style={styles.th}>Kid</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Public key</th>
            <th style={styles.th}>Created</th>
          </tr></thead>
          <tbody>{rows.map(k => (
            <tr key={k.kid} data-testid={`signing-key-row-${k.kid}`}>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>{k.kid}</td>
              <td style={styles.td}>
                <Tag color={k.status === "active" ? "safe" : "neutral"} testid={`signing-status-${k.kid}`}>{k.status}</Tag>
              </td>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12, color: "var(--hs-text-muted)" }}>
                {k.public_b64?.slice(0, 24)}…
              </td>
              <td style={styles.td}><Time value={k.created_at} /></td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={4} style={{ ...styles.td, color: "var(--hs-text-muted)", fontSize: 12 }}>
              No persisted signing key yet — the server is signing with the ephemeral
              settings-derived key. Press <em>Rotate now</em> to promote a persisted key.
            </td></tr>
          )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Audit() {
  const loadAudit = useCallback(() => api.audit("limit=100"), []);
  const { data, error, loading, reload } = useApi(loadAudit, []);
  const rows = data && data.entries ? data.entries : [];
  if (!data && !error && loading) return <div>Loading…</div>;
  return (
    <div data-testid="audit-page">
      <h2>Audit log</h2>
      {error && <ErrorNote message={error} onRetry={reload} />}
      <table style={styles.tbl}>
        <thead><tr><th style={styles.th}>When</th><th style={styles.th}>Actor</th><th style={styles.th}>Action</th><th style={styles.th}>Entity</th><th style={styles.th}>Req id</th></tr></thead>
        <tbody>{rows.map((a, i) => (
          <tr key={i}>
            <td style={styles.td}><Time value={a.created_at} /></td><td style={styles.td}>{a.actor_email_or_name}</td>
            <td style={styles.td}>{a.action}</td><td style={styles.td}>{a.entity} {a.entity_id?.slice(0, 8)}</td>
            <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)", fontSize: 12 }}>{a.request_id?.slice(0, 8)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function AdminApp() {
  // undefined = still asking the server; null = not signed in. Without the loading state, a reload
  // or deep link (e.g. /admin/items) was redirected to /admin/login before the session check
  // finished and then dropped on the dashboard.
  const [me, setMe] = useState(undefined);
  const [backendDown, setBackendDown] = useState(null);

  const loadMe = useCallback(async () => {
    setBackendDown(null);
    try {
      const r = await api.me();
      setMe(r.data);
      return r;
    } catch (err) {
      if (isAuthBounce(err) || err.status === 401 || err.status === 403) {
        setMe(null);
        return null;
      }
      if (err.timeout || err.backendDown || (typeof err.status === "number" && err.status >= 500)) {
        setMe(undefined);
        setBackendDown(err);
        return null;
      }
      setMe(null);
      return null;
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  const navigate = useNavigate();
  const loc = useLocation();
  // Each page gets its own boundary (reset on navigation): a crash shows the error, not a blank page.
  const page = (name, el) => <AdminErrorBoundary name={name} resetKey={loc.pathname}>{el}</AdminErrorBoundary>;
  // Logout: clear the app's own session first, then (Cloudflare Access mode) end the Access session at the
  // Access logout endpoint with a top-level navigation, otherwise the surviving Access session would sign the
  // admin straight back in.
  const onLogout = async () => {
    const mode = me && me.mode;
    try { await api.logout(); } catch (_) { /* nothing to clear */ }
    clearBounce();
    setMe(null);
    if (mode === "cf_access") { window.location.assign(CF_ACCESS_LOGOUT_URL); return; }
    navigate("/admin/login");
  };
  if (backendDown) return <BackendDown error={backendDown} onRetry={loadMe} />;
  if (me === undefined) return <div style={{ ...styles.bg, padding: 24 }} data-testid="admin-loading">Loading…</div>;
  if (me === null) return <Routes><Route path="/login" element={<Login onIn={setMe} />} /><Route path="*" element={<Navigate to="/admin/login" replace />} /></Routes>;
  return (
    <TimeZoneProvider>
    <div style={styles.bg}>
      <Nav me={me} onLogout={onLogout} />
      <main className="hs-admin-main">
        <Routes>
          <Route path="/" element={page("Dashboard", <Dashboard />)} />
          <Route path="/review" element={page("Review", <Review />)} />
          <Route path="/auto-confirm" element={page("Auto-confirm", <AutoConfirm styles={styles} />)} />
          <Route path="/items" element={page("Items", <Items />)} />
          <Route path="/items/:id" element={page("Item detail", <ItemDetail />)} />
          <Route path="/sources" element={page("Sources", <Sources />)} />
          <Route path="/stats" element={page("Stats", <StatsPage styles={styles} />)} />
          <Route path="/disclosures" element={page("Disclosures", <Disclosures />)} />
          <Route path="/appeals" element={page("Appeals", <Appeals />)} />
          <Route path="/submissions" element={page("Submissions", <Submissions />)} />
          <Route path="/waitlist" element={page("Waitlist", <Waitlist />)} />
          <Route path="/user-reports" element={page("User reports", <UserReports styles={styles} />)} />
          <Route path="/profiles" element={page("Profiles", <ProfilesList />)} />
          <Route path="/blocklist" element={page("Blocklist", <Blocklist styles={styles} />)} />
          <Route path="/privacy" element={page("Privacy requests", <PrivacyRequests styles={styles} />)} />
          <Route path="/reports" element={page("Reports", <AdminReports />)} />
          <Route path="/api-keys" element={page("API keys", <ApiKeys />)} />
          <Route path="/signing-keys" element={page("Signing keys", <SigningKeys />)} />
          <Route path="/audit" element={page("Audit", <Audit />)} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
    </TimeZoneProvider>
  );
}
